import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Timer as TimerIcon } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Switch } from "../components/ui/Switch";
import { useAdaptersApi } from '../apis/adapters-apis';
import { Adapter } from '../types';
import { formatHelpText } from '../config/helptext-config';
import { testAdapterPageHelpText as testPageHelpText } from '../config/testadapterpage-helptext-config';
import { FormField } from '../components/FormField';

interface TestTimerProps {
  isRunning: boolean;
  adapterTimeout?: number | string;
}

const TestTimer: React.FC<TestTimerProps> = ({ isRunning, adapterTimeout }) => {
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isRunning) {
      setElapsedTime(0);
      
      intervalId = setInterval(() => {
        setElapsedTime(prev => prev !== null ? prev + 1 : 0);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRunning]);

  if (elapsedTime === null) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <TimerIcon className="h-4 w-4" />
      <span>
        {elapsedTime}s / {adapterTimeout || 'Default'}s
      </span>
    </div>
  );
};

const TestAdapterPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const adaptersApi = useAdaptersApi();
  
  const [adapter, setAdapter] = useState<Adapter | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
  const [testMode, setTestMode] = useState(true);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [isTestingInProgress, setIsTestingInProgress] = useState(false);

  const fetchAdapter = useCallback(async () => {
    if (!id) return;
    
    try {
      const fetchedAdapter = await adaptersApi.getAdapter(id);
      
      if (fetchedAdapter) {
        setAdapter(fetchedAdapter);
        setTestMode(fetchedAdapter.adapterParameters.testMode);
        const initialValues: Record<string, string> = {};
        fetchedAdapter.adapterParameters.parameters.forEach(param => {
          initialValues[param.key] = '';
        });
        setParameterValues(initialValues);
      } else {
        addToast({
          title: "Error",
          description: "Adapter not found",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching adapter:', error);
      addToast({
        title: "Error",
        description: "Failed to load adapter details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, adaptersApi, addToast]);

  useEffect(() => {
    fetchAdapter();
  }, []);

  const formatParameterValue = (value: string, type: string): any => {
    switch (type) {
      case 'int':
        return parseInt(value);
      case 'float':
        return parseFloat(value);
      case 'bool':
        return value.toLowerCase() === 'true';
      case 'list':
      case 'dict':
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      default: // handles 'str' and any other types
        return value;
    }
  };

  const buildRequestBody = (): Record<string, any> => {
    if (!adapter) {
      throw new Error('Adapter is not available');
    }
    
    const formattedParameters: Record<string, any> = {};
    
    adapter.adapterParameters.parameters.forEach(param => {
      const value = parameterValues[param.key]?.trim();
      if (value) {
        formattedParameters[param.key] = formatParameterValue(value, param.type);
      }
    });

    return {
      testMode,
      adapterTimeout: adapter.adapterTimeout,
      adapterUrlEndpoint: adapter.adapterUrlEndpoint,
      adapterTargetUrl: adapter.adapterTargetUrl,
      adapterParameters: formattedParameters,
    };
  };

  const handleTestResponse = (response: any) => {
    setTestResponse(response);
    
    if (!response) {
      const errorMessage = 'Test failed. No response received.';
      setTestError(errorMessage);
      addToast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } else {
      setTestError(null);
      addToast({
        title: "Success",
        description: "Test completed successfully",
        variant: "default",
      });
    }
  };

  const handleTestError = (error: any) => {
    console.error('Test error:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    setTestError(errorMessage);
    
    addToast({
      title: "Error",
      description: error instanceof Error ? error.message : "Test failed. Please try again.",
      variant: "destructive",
    });
  };

  const handleTest = async () => {
    if (!adapter) return;
  
    setIsTestingInProgress(true);
    setIsTestRunning(true);
    setTestResponse(null);
    setTestError(null);
  
    try {
      const requestBody = buildRequestBody();
      console.log('Test request body:', requestBody);
  
      const response = await adaptersApi.testAdapter(adapter.adapterName || '', requestBody);
      console.log('Test response:', response);
  
      handleTestResponse(response);
    } catch (error) {
      handleTestError(error);
    } finally {
      setIsTestingInProgress(false);
      setIsTestRunning(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Adapter...</p>
      </div>
    );
  }

  if (!adapter) {
    return (
      <Card className="max-w-2xl mx-auto m-4">
        <div className="p-6">
          <p className="text-center text-muted-foreground">Adapter not found.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <Card className="max-w-3xl mx-auto">
        <div className="flex flex-row items-center justify-between p-6 pb-2">
          <h2 className="text-2xl font-semibold">Test Adapter</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/studio/admin/adapters/edit/${adapter.adapterId}`, {
                state: { startEditing: true }
              })}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/studio/admin/adapters')}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Adapters
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
            <FormField label="Adapter Name">
              <p className="text-gray-900">{adapter.adapterName}</p>
            </FormField>

            <FormField
              label="Adapter Timeout"
              helpText={formatHelpText(testPageHelpText.adapterTimeout)}
            >
              <p className="text-gray-900">{adapter.adapterTimeout || 'Default'}</p>
            </FormField>

            {adapter.adapterAnnotation && (
              <FormField
                label="Annotation"
                helpText={formatHelpText(testPageHelpText.annotation)}
              >
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono bg-white p-4 rounded border">
                  {adapter.adapterAnnotation}
                </pre>
              </FormField>
            )}
          </div>

          <div className="bg-slate-50 p-6 rounded-lg">
            <FormField
              label="Test Mode"
              helpText={formatHelpText(testPageHelpText.testMode)}
            >
              <div className="flex items-center justify-between mt-2">
                <p className="text-gray-500">Enable or disable test mode for this adapter</p>
                <div className="flex items-center gap-4">
                  <Switch
                    checked={testMode}
                    onCheckedChange={setTestMode}
                    disabled={isTestingInProgress}
                  />
                  <span className={`text-lg font-medium ${testMode ? 'text-blue-600' : 'text-gray-400'}`}>
                    {testMode ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </FormField>
          </div>

          <FormField
            label="Input Parameters"
            helpText={formatHelpText(testPageHelpText.inputParameters)}
          >
            <div className="space-y-4 mt-2">
              {adapter.adapterParameters.parameters.map((param) => (
                <div key={param.key} className="space-y-2">
                  <label className="text-sm font-medium">
                    {param.key}
                    <span className="text-gray-500 ml-2">({param.type})</span>
                  </label>
                  {param.description && (
                    <p className="text-sm text-gray-500">{param.description}</p>
                  )}
                  <Input
                    value={parameterValues[param.key]}
                    onChange={(e) => setParameterValues(prev => ({
                      ...prev,
                      [param.key]: e.target.value
                    }))}
                    placeholder={param.example || `Enter ${param.key}`}
                    disabled={isTestingInProgress}
                  />
                  {param.options && (
                    <p className="text-xs text-gray-500">Options: {param.options}</p>
                  )}
                </div>
              ))}
            </div>
          </FormField>

          <FormField
            label="Response"
            helpText={formatHelpText(testPageHelpText.results)}
          >
            <div className="bg-slate-50 rounded-lg min-h-8 mt-2">
              <div className={`p-4 transition-all duration-200 ${testResponse || testError ? 'max-h-96 overflow-y-auto' : 'h-12'}`}>
                {isTestingInProgress ? (
                  <div className="flex items-center">
                    <Spinner className="w-4 h-4" />
                    <span className="ml-2 font-mono text-sm">Processing...</span>
                  </div>
                ) : testError ? (
                  <div className="text-red-500 font-mono text-sm">{testError}</div>
                ) : testResponse ? (
                  <pre className="whitespace-pre-wrap font-mono text-sm">
                    {JSON.stringify(testResponse, null, 2)}
                  </pre>
                ) : (
                  <div className="font-mono text-sm text-slate-600">
                    Enter values for Parameters and click Test button to run Adapter.
                  </div>
                )}
              </div>
            </div>
          </FormField>

          <div className="flex justify-end items-center gap-4 pt-4">
            <TestTimer 
              isRunning={isTestRunning} 
              adapterTimeout={adapter?.adapterTimeout}
            />
            <Button
              onClick={handleTest}
              disabled={isTestingInProgress}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isTestingInProgress ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Testing...
                </>
              ) : (
                'Test'
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TestAdapterPage; 